import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import UnsavedChangesNotice from '../components/ui/UnsavedChangesNotice';
import { useConfirmation } from '../contexts/ConfirmationContext';
import React, { useEffect, useState, useCallback } from 'react';
import { databases } from '../config/appwrite';
import { appwriteConfig } from '../config/appwrite';
import { Query } from 'appwrite';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { UserIcon, BookOpenIcon, HandThumbUpIcon, PencilIcon, TrashIcon, UserPlusIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { AuthService } from '../services/authService';
import { User, Book } from '../types';
import toast from 'react-hot-toast';
import { ReviewWizard } from '../components/ReviewWizard';
import { reviewService, Review } from '../services/reviewService';


export const BooksPage: React.FC = () => {
  const { user, hasRole } = useAuth();
  const confirm = useConfirmation();
  const [books, setBooks] = useState<Book[]>([]);
  const [myBooks, setMyBooks] = useState<Book[]>([]);
  const [assignedBooks, setAssignedBooks] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showAuthorDialog, setShowAuthorDialog] = useState(false);
  const [authors, setAuthors] = useState<User[]>([]);
  const [selectedAuthor, setSelectedAuthor] = useState<User | null>(null);
  const [showReviewWizard, setShowReviewWizard] = useState(false);
  const [reviewBook, setReviewBook] = useState<Book | null>(null);
  const [userReviews, setUserReviews] = useState<Review[]>([]);
  const [showSuggestionListModal, setShowSuggestionListModal] = useState(false);
  const [generatedList, setGeneratedList] = useState<string>('');
  const [lastGeneratedBookIds, setLastGeneratedBookIds] = useState<string[]>([]);
  const [reviewState, setReviewState] = useState({ dirty: false, busy: false });
  const [listCopied, setListCopied] = useState(false);
  const [reload, setReload] = useState(0);
  const handleReviewDirty = useCallback((dirty: boolean, busy: boolean) => setReviewState(previous => previous.dirty === dirty && previous.busy === busy ? previous : { dirty, busy }), []);

  useEffect(() => {
    const fetchBooks = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true); setError(null);
        const response = await databases.listDocuments(
          appwriteConfig.databaseId,
          appwriteConfig.collections.books,
          [
            Query.orderDesc('$createdAt'), 
            Query.limit(100)
          ]
        );
        
        const allBooks = response.documents as unknown as Book[];
        
        // Lade alle Rezensionen (wir filtern später nach User und Buch)
        try {
          const allReviews = await reviewService.getAllReviews();
          setUserReviews(allReviews);
        } catch (reviewError) {
          console.warn('Rezensionen konnten nicht geladen werden:', reviewError);
          // Setze leeres Array als Fallback
          setUserReviews([]);
        }
        
        // Filtere Bücher ohne Interessenten heraus
        const availableBooks = allBooks.filter(book => !book.interessent);
        setBooks(availableBooks);
        
        // Filtere Bücher des eingeloggten Autors als Interessent
        const userBooks = allBooks.filter(book => book.interessent === user.name);
        setMyBooks(userBooks);
        
        // Filtere vergebene Bücher (nur für Admins/Redakteure)
        if (hasRole(['administrator', 'redakteur'])) {
          const booksWithInteressent = allBooks.filter(
            book => book.interessent && book.interessent !== user.name
          );
          setAssignedBooks(booksWithInteressent);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Bücher:', err);
        setError('Bücher konnten nicht geladen werden.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBooks();
  }, [user, hasRole, reload]);

  // Autoren laden (nur für Admins/Redakteure)
  useEffect(() => {
    const fetchAuthors = async () => {
      if (!user || !hasRole(['administrator', 'redakteur'])) return;
      
      try {
        const allUsers = await AuthService.getAllUsers();
        setAuthors(allUsers);
      } catch (err) {
        console.error('Fehler beim Laden der Autoren:', err);
      }
    };

    fetchAuthors();
  }, [user, hasRole]);

  const handleRequestReview = (book: Book) => {
    setSelectedBook(book);
    setShowConfirmDialog(true);
  };

  const handleConfirmReview = async () => {
    if (!selectedBook || !user || isUpdating) return;

    try {
      setIsUpdating(true);
      await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.collections.books,
        selectedBook.$id,
        {
          interessent: user.name
        }
      );

      // Entferne das Buch aus der lokalen Liste
      setBooks(prevBooks => prevBooks.filter(book => book.$id !== selectedBook.$id));
      setShowConfirmDialog(false);
      setSelectedBook(null);
      toast.success('Rezension angefordert.');
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Buches:', err);
      toast.error('Das Buch konnte nicht angefordert werden. Bitte versuche es erneut.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelReview = () => {
    setShowConfirmDialog(false);
    setSelectedBook(null);
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  // Prüfe ob bereits eine Rezension für ein Buch eingereicht wurde
  const hasReviewForBook = (bookId: string) => {
    return userReviews.some(review => {
      const bookMatch = review.buchId === bookId;
      const userMatch = review.autorId === user?.id;
      return bookMatch && userMatch;
    });
  };

  // Admin-Funktionen
  const handleRemoveBook = async (book: Book) => {
    if (!(await confirm({ title: 'Buch entfernen?', description: `Das Buch „${book.titel}“ wird aus der Bücherliste entfernt.`, confirmLabel: 'Buch entfernen', danger: true }))) {
      return;
    }

    try {
      await databases.deleteDocument(
        appwriteConfig.databaseId,
        appwriteConfig.collections.books,
        book.$id
      );

      // Entferne das Buch aus der lokalen Liste
      setBooks(prevBooks => prevBooks.filter(b => b.$id !== book.$id));
      setMyBooks(prevMyBooks => prevMyBooks.filter(b => b.$id !== book.$id));
      toast.success('Buch erfolgreich entfernt');
    } catch (err) {
      console.error('Fehler beim Entfernen des Buches:', err);
      toast.error('Fehler beim Entfernen des Buches');
    }
  };

  const handleAssignBook = (book: Book) => {
    setSelectedBook(book);
    setShowAuthorDialog(true);
  };

  const handleConfirmAssign = async () => {
    if (!selectedBook || !selectedAuthor || isUpdating) return;

    try {
      setIsUpdating(true);
      await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.collections.books,
        selectedBook.$id,
        {
          interessent: selectedAuthor.name
        }
      );

      // Entferne das Buch aus der verfügbaren Liste
      setBooks(prevBooks => prevBooks.filter(book => book.$id !== selectedBook.$id));
      
      // Füge das Buch zu "Meine Bücher" hinzu, falls der Admin sich selbst zuweist
      if (selectedAuthor.id === user!.id) {
        setMyBooks(prevMyBooks => [...prevMyBooks, { ...selectedBook, interessent: selectedAuthor.name }]);
      }
      
      setShowAuthorDialog(false);
      setSelectedBook(null);
      setSelectedAuthor(null);
      toast.success(`Buch erfolgreich an ${selectedAuthor.name} zugewiesen`);
    } catch (err) {
      console.error('Fehler beim Zuweisen des Buches:', err);
      toast.error('Fehler beim Zuweisen des Buches');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelAssign = () => {
    setShowAuthorDialog(false);
    setSelectedBook(null);
    setSelectedAuthor(null);
  };

  const handleUnassignBook = async (book: Book) => {
    if (!(await confirm({ title: 'Zuweisung aufheben?', description: `Das Buch „${book.titel}“ ist anschließend wieder verfügbar.`, confirmLabel: 'Zuweisung aufheben', danger: true }))) {
      return;
    }

    try {
      await databases.updateDocument(
        appwriteConfig.databaseId,
        appwriteConfig.collections.books,
        book.$id,
        {
          interessent: null
        }
      );

      // Entferne das Buch aus der vergebenen Liste
      setAssignedBooks(prevBooks => prevBooks.filter(b => b.$id !== book.$id));
      // Füge es wieder zu den verfügbaren Büchern hinzu
      setBooks(prevBooks => [...prevBooks, { ...book, interessent: undefined }]);
      toast.success('Zuweisung erfolgreich aufgehoben');
    } catch (err) {
      console.error('Fehler beim Aufheben der Zuweisung:', err);
      toast.error('Fehler beim Aufheben der Zuweisung');
    }
  };

  // Vorschlagsliste generieren
  const handleGenerateSuggestionList = async () => {
    if (isUpdating) return;
    if (generatedList && !listCopied && !(await confirm({ title: 'Vorschlagsliste ersetzen?', description: 'Die aktuelle Liste wurde noch nicht kopiert.', confirmLabel: 'Neue Liste erstellen' }))) return;
    try {
      setIsUpdating(true);
      
      // Alle Bücher aus DB holen (inklusive aller Kategorien)
      const response = await databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.collections.books,
        [Query.limit(1000)] // Erhöhe Limit für alle Bücher
      );
      
      const allBooks = response.documents as unknown as Book[];
      
      // Kategorisieren
      const neu = allBooks.filter(book => (book.vorgeschlagen || 0) === 0);
      const erneut = allBooks.filter(book => (book.vorgeschlagen || 0) === 1);
      const letztesMal = allBooks.filter(book => (book.vorgeschlagen || 0) === 2);
      
      // Bücher merken, die wir updaten (vorgeschlagen 0, 1, 2)
      const booksToUpdate = [...neu, ...erneut, ...letztesMal];
      const bookIdsToUpdate = booksToUpdate.map(book => book.$id);
      setLastGeneratedBookIds(bookIdsToUpdate);
      
      // Textliste generieren
      let text = '';
      
      if (neu.length > 0) {
        text += 'NEU:\n';
        neu.forEach(book => {
          text += `${book.autor}: ${book.titel}\n`;
        });
        text += '\n';
      }
      
      if (erneut.length > 0) {
        text += 'Erneut vorgeschlagen:\n';
        erneut.forEach(book => {
          text += `${book.autor}: ${book.titel}\n`;
        });
        text += '\n';
      }
      
      if (letztesMal.length > 0) {
        text += 'Zum letzten Mal vorgeschlagen:\n';
        letztesMal.forEach(book => {
          text += `${book.autor}: ${book.titel}\n`;
        });
      }
      
      setGeneratedList(text.trim());
      setShowSuggestionListModal(true); setListCopied(false);
      
      // Counter +1 für alle Bücher aus der Liste (vorgeschlagen 0, 1, 2)
      const updatePromises = booksToUpdate.map(book => 
        databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.collections.books,
          book.$id,
          {
            vorgeschlagen: (book.vorgeschlagen || 0) + 1
          }
        )
      );
      
      await Promise.all(updatePromises);
      
      // Lokale State aktualisieren
      setBooks(prevBooks => 
        prevBooks.map(book => 
          bookIdsToUpdate.includes(book.$id) 
            ? { ...book, vorgeschlagen: (book.vorgeschlagen || 0) + 1 }
            : book
        )
      );
      setMyBooks(prevBooks => 
        prevBooks.map(book => 
          bookIdsToUpdate.includes(book.$id) 
            ? { ...book, vorgeschlagen: (book.vorgeschlagen || 0) + 1 }
            : book
        )
      );
      setAssignedBooks(prevBooks => 
        prevBooks.map(book => 
          bookIdsToUpdate.includes(book.$id) 
            ? { ...book, vorgeschlagen: (book.vorgeschlagen || 0) + 1 }
            : book
        )
      );
      
      toast.success('Vorschlagsliste generiert und Counter aktualisiert');
    } catch (err) {
      console.error('Fehler beim Generieren der Vorschlagsliste:', err);
      toast.error('Fehler beim Generieren der Vorschlagsliste');
    } finally {
      setIsUpdating(false);
    }
  };

  // Testmodus rückgängig (-1)
  const handleDecrementCounters = async () => {
    if (lastGeneratedBookIds.length === 0) {
      toast.error('Keine Liste vorhanden. Bitte zuerst eine Liste generieren.');
      return;
    }

    if (!(await confirm({ title: 'Vorschlagszähler zurücksetzen?', description: `Bei ${lastGeneratedBookIds.length} Büchern wird der Zähler jeweils um eins verringert.`, confirmLabel: 'Zähler verringern', danger: true }))) {
      return;
    }

    try {
      setIsUpdating(true);
      
      // Counter -1 für alle Bücher aus der letzten Liste
      const updatePromises = lastGeneratedBookIds.map(async (bookId) => {
        // Aktuellen Wert aus DB holen
        const response = await databases.getDocument(
          appwriteConfig.databaseId,
          appwriteConfig.collections.books,
          bookId
        );
        const currentBook = response as unknown as Book;
        const newValue = Math.max(0, (currentBook.vorgeschlagen || 0) - 1); // Nicht unter 0
        
        return databases.updateDocument(
          appwriteConfig.databaseId,
          appwriteConfig.collections.books,
          bookId,
          {
            vorgeschlagen: newValue
          }
        );
      });
      
      await Promise.all(updatePromises);
      
      // Lokale State aktualisieren
      setBooks(prevBooks => 
        prevBooks.map(book => 
          lastGeneratedBookIds.includes(book.$id) 
            ? { ...book, vorgeschlagen: Math.max(0, (book.vorgeschlagen || 0) - 1) }
            : book
        )
      );
      setMyBooks(prevBooks => 
        prevBooks.map(book => 
          lastGeneratedBookIds.includes(book.$id) 
            ? { ...book, vorgeschlagen: Math.max(0, (book.vorgeschlagen || 0) - 1) }
            : book
        )
      );
      setAssignedBooks(prevBooks => 
        prevBooks.map(book => 
          lastGeneratedBookIds.includes(book.$id) 
            ? { ...book, vorgeschlagen: Math.max(0, (book.vorgeschlagen || 0) - 1) }
            : book
        )
      );
      
      // Liste leeren nach Rückgängig
      setLastGeneratedBookIds([]);
      
      toast.success('Counter erfolgreich reduziert (-1)');
    } catch (err) {
      console.error('Fehler beim Reduzieren der Counter:', err);
      toast.error('Fehler beim Reduzieren der Counter');
    } finally {
      setIsUpdating(false);
    }
  };

  // In Zwischenablage kopieren
  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedList)
      .then(() => {
        setListCopied(true);
        toast.success('In Zwischenablage kopiert!');
      })
      .catch(() => {
        toast.error('Fehler beim Kopieren');
      });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 text-lg font-medium mb-2">Fehler beim Laden</div>
        <div role="alert" className="text-gray-600">{error}</div><button className="btn-secondary mt-4" onClick={() => setReload(n => n + 1)}>Erneut versuchen</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Bücher und Rezensionen" description="Wähle ein Buch, behalte deine Zuweisungen im Blick und reiche deine Rezension ein." />
      <UnsavedChangesNotice dirty={reviewState.dirty || (!!generatedList && !listCopied)} busy={isUpdating || reviewState.busy} localOnly />
      {/* Vergebene Bücher - nur für Admins/Redakteure (oben priorisiert) */}
      {hasRole(['administrator', 'redakteur']) && assignedBooks.length > 0 && (
        <>
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BookOpenIcon className="w-8 h-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <h2 className="text-2xl font-bold text-gray-900">Vergebene Bücher</h2>
                <p className="mt-1 text-sm text-gray-600">
                  {assignedBooks.length} {assignedBooks.length !== 1 ? 'Bücher' : 'Buch'} an Autoren vergeben
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  Übersicht über alle vergebenen Bücher und deren Interessenten.
                </p>
              </div>
            </div>
          </div>

          {/* Vergebene Bücher - Liste */}
          <div className="space-y-6">
            {assignedBooks.map((book) => (
              <div key={book.$id} className="card hover:shadow-md transition-shadow duration-200 border-l-4 border-orange-500">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Buch-Cover */}
                  <div className="flex-shrink-0 mx-auto md:mx-0">
                    {book['bild-url'] ? (
                      <div
                        className="w-48 h-72 overflow-hidden rounded-lg relative cursor-pointer hover:opacity-90 transition-opacity duration-200"
                        onClick={() => handleImageClick(book['bild-url']!)}
                      >
                        <img
                          src={book['bild-url']}
                          alt={book.titel}
                          className="absolute top-0 left-0 w-full h-full object-cover object-center"
                          style={{ aspectRatio: '9 / 16' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 hidden">
                          <BookOpenIcon className="w-12 h-12" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-48 h-72 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                        <BookOpenIcon className="w-12 h-12" />
                      </div>
                    )}
                  </div>

                  {/* Buch-Informationen - Rechts */}
                  <div className="flex-1 min-w-0">
                    <div className="space-y-3">
                      <h2 className="text-base font-normal text-gray-900">
                        {book.titel}
                      </h2>

                      <div className="flex items-center text-gray-600">
                        <UserIcon className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
                        <span className="text-sm font-normal">{book.autor}</span>
                      </div>

                      {/* Vorgeschlagen-Anzahl */}
                      <div className="flex items-center text-gray-600">
                        <HandThumbUpIcon className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
                        <span className="text-sm font-normal">{book.vorgeschlagen || 0} mal vorgeschlagen</span>
                      </div>

                      {/* Interessent */}
                      <div className="flex items-center text-gray-600">
                        <UserIcon className="w-4 h-4 mr-2 text-orange-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-orange-600">Vergeben an: {book.interessent}</span>
                      </div>

                      {/* Admin Buttons */}
                      <div className="pt-4 space-y-3">
                        <div className="flex flex-col md:flex-row gap-3">
                          <button
                            onClick={() => handleUnassignBook(book)}
                            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                          >
                            Zuweisung aufheben
                          </button>
                          <button
                            onClick={() => handleRemoveBook(book)}
                            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                          >
                            <TrashIcon className="w-4 h-4 mr-2" />
                            Buch entfernen
                          </button>
                        </div>

                        {/* Amazon-Link Button */}
                        {book['amazon-link'] && (
                          <a
                            href={book['amazon-link']}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
                          >
                            Bei Amazon ansehen
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Meine Bücher - nur anzeigen wenn der User Bücher hat */}
      {myBooks.length > 0 && (
        <>
          <div className="card">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BookOpenIcon className="w-8 h-8 text-green-600" />
              </div>
              <div className="ml-4">
                <h2 className="text-2xl font-bold text-gray-900">Meine Bücher</h2>
                <p className="mt-1 text-sm text-gray-600">
                  {myBooks.length} {myBooks.length !== 1 ? 'Bücher' : 'Buch'} für Rezension zugesagt
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  Diese Bücher wurden dir für eine Rezension zugesagt. Du kannst sie jederzeit bearbeiten.
                </p>
              </div>
            </div>
          </div>

          {/* Meine Bücher - Liste */}
          <div className="space-y-6">
            {myBooks.map((book) => (
              <div key={book.$id} className="card hover:shadow-md transition-shadow duration-200">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Buch-Cover */}
                  <div className="flex-shrink-0 mx-auto md:mx-0">
                    {book['bild-url'] ? (
                      <div 
                        className="w-48 h-72 overflow-hidden rounded-lg relative cursor-pointer hover:opacity-90 transition-opacity duration-200"
                        onClick={() => handleImageClick(book['bild-url']!)}
                      >
                        <img
                          src={book['bild-url']}
                          alt={book.titel}
                          className="absolute top-0 left-0 w-full h-full object-cover object-center"
                          style={{ aspectRatio: '9 / 16' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 hidden">
                          <BookOpenIcon className="w-12 h-12" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-48 h-72 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                        <BookOpenIcon className="w-12 h-12" />
                      </div>
                    )}
                  </div>

                  {/* Buch-Informationen - Rechts */}
                  <div className="flex-1 min-w-0">
                    <div className="space-y-3">
                      <h2 className="text-base font-normal text-gray-900">
                        {book.titel}
                      </h2>
                      
                      <div className="flex items-center text-gray-600">
                        <UserIcon className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
                        <span className="text-sm font-normal">{book.autor}</span>
                      </div>

                      {/* Vorgeschlagen-Anzahl */}
                      <div className="flex items-center text-gray-600">
                        <HandThumbUpIcon className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
                        <span className="text-sm font-normal">{book.vorgeschlagen || 0} mal vorgeschlagen</span>
                      </div>

                      {/* Rezension einreichen Button - nur anzeigen wenn noch keine Rezension eingereicht */}
                      {!hasReviewForBook(book.$id) && (
                        <div className="pt-4">
                          <button
                            onClick={() => {
                              setReviewBook(book);
                              setShowReviewWizard(true);
                            }}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                          >
                            <PencilIcon className="w-4 h-4 mr-2" />
                            Rezension einreichen
                          </button>
                        </div>
                      )}

                      {/* Status anzeigen wenn Rezension bereits eingereicht */}
                      {hasReviewForBook(book.$id) && (
                        <div className="pt-4">
                          <div className="inline-flex items-center px-3 py-2 text-sm font-medium text-green-800 bg-green-100 rounded-md">
                            <CheckCircleIcon className="w-4 h-4 mr-2" />
                            Rezension bereits eingereicht
                          </div>
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="pt-4 space-y-3">
                        {/* Admin/Redakteur Buttons */}
                        {hasRole(['administrator', 'redakteur']) && (
                          <div className="flex flex-col md:flex-row gap-3">
                            <button
                              onClick={() => handleRemoveBook(book)}
                              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                            >
                              <TrashIcon className="w-4 h-4 mr-2" />
                              Buch entfernen
                            </button>
                          </div>
                        )}

                        {/* Amazon-Link Button */}
                        {book['amazon-link'] && (
                          <a
                            href={book['amazon-link']}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
                          >
                            Bei Amazon ansehen
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BookOpenIcon className="w-8 h-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <h2 className="text-2xl font-bold text-gray-900">Bücher für Rezensenten</h2>
              <p className="mt-1 text-sm text-gray-600">
                {books.length} {books.length !== 1 ? 'Bücher' : 'Buch'} verfügbar
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Wähle ein interessantes Buch aus und beginne deine Rezension. Alle Bücher sind kostenlos verfügbar.
              </p>
            </div>
          </div>

          {/* Admin/Redakteur Buttons */}
          {hasRole(['administrator', 'redakteur']) && (
            <div className="flex flex-col gap-3 ml-4">
              <button
                onClick={handleGenerateSuggestionList}
                disabled={isUpdating}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isUpdating ? 'Wird generiert...' : 'Vorschlagsliste generieren'}
              </button>
              {generatedList && <button className="btn-secondary" onClick={() => setShowSuggestionListModal(true)}>Vorschlagsliste ansehen</button>}
              <button
                onClick={handleDecrementCounters}
                disabled={isUpdating || lastGeneratedBookIds.length === 0}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Testmodus rückgängig (-1)
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bücher-Grid */}
      {books.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <BookOpenIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Bücher verfügbar</h3>
            <p className="text-gray-600">
              Es sind momentan keine Bücher für Rezensenten verfügbar.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {books.map((book) => (
            <div key={book.$id} className="card hover:shadow-md transition-shadow duration-200">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Buch-Cover */}
                <div className="flex-shrink-0 mx-auto md:mx-0">
                  {book['bild-url'] ? (
                    <div 
                      className="w-48 h-72 overflow-hidden rounded-lg relative cursor-pointer hover:opacity-90 transition-opacity duration-200"
                      onClick={() => handleImageClick(book['bild-url']!)}
                    >
                      <img
                        src={book['bild-url']}
                        alt={book.titel}
                        className="absolute top-0 left-0 w-full h-full object-cover object-center"
                        style={{ aspectRatio: '9 / 16' }}
                        onError={(e) => {
                          // Fallback wenn Bild nicht lädt
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      {/* Fallback wenn Bild nicht lädt */}
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 hidden">
                        <BookOpenIcon className="w-12 h-12" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-48 h-72 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                      <BookOpenIcon className="w-12 h-12" />
                    </div>
                  )}
                </div>

                {/* Buch-Informationen - Rechts */}
                <div className="flex-1 min-w-0">
                  <div className="space-y-3">
                    <h2 className="text-base font-normal text-gray-900">
                      {book.titel}
                    </h2>
                    
                    <div className="flex items-center text-gray-600">
                      <UserIcon className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
                      <span className="text-sm font-normal">{book.autor}</span>
                    </div>

                    {/* Vorgeschlagen-Anzahl */}
                    <div className="flex items-center text-gray-600">
                      <HandThumbUpIcon className="w-4 h-4 mr-2 text-gray-500 flex-shrink-0" />
                      <span className="text-sm font-normal">{book.vorgeschlagen || 0} mal vorgeschlagen</span>
                    </div>

                    {/* Zusätzliche Informationen */}
                    {book.interessent && (
                      <div className="flex items-center text-gray-600">
                        <span className="text-sm font-normal text-green-600">Interessent: {book.interessent}</span>
                      </div>
                    )}

                    {/* Buttons */}
                    <div className="pt-4 space-y-3">
                      {/* Rezensions-Button (für normale Autoren) */}
                      {!hasRole(['administrator', 'redakteur']) && (
                        <button
                          onClick={() => handleRequestReview(book)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                        >
                          <PencilIcon className="w-4 h-4 mr-2" />
                          Rezensionsexemplar bestellen
                        </button>
                      )}

                      {/* Admin/Redakteur Buttons */}
                      {hasRole(['administrator', 'redakteur']) && (
                        <div className="flex flex-col md:flex-row gap-3">
                          <button
                            onClick={() => handleAssignBook(book)}
                            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                          >
                            <UserPlusIcon className="w-4 h-4 mr-2" />
                            Buch zuweisen
                          </button>
                          <button
                            onClick={() => handleRemoveBook(book)}
                            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                          >
                            <TrashIcon className="w-4 h-4 mr-2" />
                            Buch entfernen
                          </button>
                        </div>
                      )}

                      {/* Amazon-Link Button */}
                      {book['amazon-link'] && (
                        <a
                          href={book['amazon-link']}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200"
                        >
                          Bei Amazon ansehen
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bestätigungsdialog */}
      <ConfirmDialog open={showConfirmDialog && !!selectedBook} title="Rezension anfordern?"
        description={`Möchtest du „${selectedBook?.titel || ''}“ von ${selectedBook?.autor || ''} für eine Rezension zugesendet bekommen?`}
        confirmLabel="Rezension anfordern" busy={isUpdating} onClose={handleCancelReview} onConfirm={handleConfirmReview} />

      {/* Bild-Modal */}
      {showImageModal && selectedImage && <Modal title="Buchcover" onClose={handleCloseImageModal} wide>
        <img src={selectedImage} alt="Buchcover vergrößert" className="mx-auto max-h-[75vh] max-w-full object-contain" />
      </Modal>}

      {/* Autoren-Auswahl-Dialog */}
      {showAuthorDialog && selectedBook && (
        <Modal title="Buch einem Autor zuweisen" onClose={handleCancelAssign} busy={isUpdating}>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 mb-4 text-center">
                  <strong>{selectedBook.titel}</strong><br />
                  von {selectedBook.autor}
                </p>
                <p className="text-sm text-gray-700 mb-3 font-medium">Autor auswählen:</p>
                
                {/* Autoren-Liste */}
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {authors.map((author) => (
                    <button
                      key={author.id}
                      onClick={() => setSelectedAuthor(author)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                        selectedAuthor?.id === author.id
                          ? 'bg-blue-100 text-blue-800 border border-blue-300'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                      }`}
                    >
                      <div className="flex items-center">
                        <UserIcon className="w-4 h-4 mr-2" />
                        {author.name}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="items-center px-4 py-3 flex justify-center gap-3">
                <button
                  onClick={handleConfirmAssign}
                  disabled={isUpdating || !selectedAuthor}
                  className="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? 'Wird zugewiesen...' : 'Zuweisen'}
                </button>
                <button
                  onClick={handleCancelAssign}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Abbrechen
                </button>
              </div>
        </Modal>
      )}

      {/* Review Wizard Modal */}
      {reviewBook && (
        <ReviewWizard
          isOpen={showReviewWizard}
          onClose={() => {
            setShowReviewWizard(false);
            setReviewBook(null); setReviewState({ dirty: false, busy: false });
          }}
          onDirtyChange={handleReviewDirty}
          book={reviewBook}
          userId={user?.id || ''}
          userName={user?.name || ''}
        />
      )}

      {/* Vorschlagsliste Modal */}
      {showSuggestionListModal && (
        <Modal title="Vorschlagsliste für Rezensenten" onClose={() => setShowSuggestionListModal(false)} wide>
              {/* Scrollbare Textliste */}
              <div className="mt-4 px-4 py-3">
                <div className="max-h-96 overflow-y-auto bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono">
                    {generatedList || 'Keine Bücher in der Liste.'}
                  </pre>
                </div>
              </div>

              {/* Buttons */}
              <div className="items-center px-4 py-3 flex justify-center gap-3">
                <button
                  onClick={handleCopyToClipboard}
                  className="px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  In Zwischenablage kopieren
                </button>
                <button
                  onClick={() => setShowSuggestionListModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                >
                  Schließen
                </button>
              </div>
        </Modal>
      )}
    </div>
  );
};
